import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'

/**
 * Elapsed time tracking hook.
 *
 * Separates the "tick every second" mechanism from the elapsed time
 * calculation. Uses Luxon for date math instead of raw Date arithmetic.
 *
 * Uses `.getTime()` for useEffect deps to avoid unnecessary re-runs
 * when Date objects change reference but not value.
 *
 * @param isActive - Whether the timer should be ticking
 * @param startTime - Start of the current task/break
 * @param shiftStartTime - Start of the entire shift
 */
export const useElapsedTimer = (
  isActive: boolean,
  startTime?: Date | null,
  shiftStartTime?: Date | null,
) => {
  const [elapsed, setElapsed] = useState(0)
  const [shiftElapsed, setShiftElapsed] = useState(0)
  const intervalRef = useRef<number | null>(null)

  // Stabilize deps using .getTime() so Date objects with the same
  // underlying value don't cause unnecessary effect re-runs
  const startTimeMs = startTime?.getTime() ?? null
  const shiftStartTimeMs = shiftStartTime?.getTime() ?? null

  useEffect(() => {
    if (!isActive) {
      setElapsed(0)
      setShiftElapsed(0)
      return
    }

    const tick = () => {
      const now = DateTime.now()

      if (startTimeMs !== null) {
        const diff = now.diff(DateTime.fromMillis(startTimeMs), 'seconds')
        setElapsed(Math.max(0, Math.floor(diff.seconds)))
      }

      if (shiftStartTimeMs !== null) {
        const diff = now.diff(DateTime.fromMillis(shiftStartTimeMs), 'seconds')
        setShiftElapsed(Math.max(0, Math.floor(diff.seconds)))
      }
    }

    tick()
    intervalRef.current = window.setInterval(tick, 1000)

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current)
    }
  }, [isActive, startTimeMs, shiftStartTimeMs])

  return { elapsed, shiftElapsed }
}

/**
 * Format duration as hours and minutes — no seconds.
 *
 * Seconds are intentionally omitted: a ticking-seconds clock invites gaming
 * the system (especially with rounding on) and is visually noisy.
 *
 * Under 24h: "HH:MM"
 * 24h+: "Xd HH:MM" (days returned separately)
 */
export const formatDuration = (
  seconds: number,
): { days: number; time: string } => {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const totalMinutes = Math.floor(safeSeconds / 60)
  const totalHours = Math.floor(totalMinutes / 60)
  const days = Math.floor(totalHours / 24)
  const h = totalHours % 24
  const m = totalMinutes % 60

  const time = [h, m].map((v) => String(v).padStart(2, '0')).join(':')
  return { days, time }
}
